import Image from 'next/image';

/**
 * Face of a hero token card: the artwork plus the "glare" layer from the Figma card
 * (Container 10 › `Column` + `glare`). Render it inside the positioned, rounded, `overflow-hidden`
 * tile wrapper; it fills that wrapper.
 *
 * Glare structure (Figma node 249:4028, designed at a 400px card):
 *   - a 396×396 clip at the top-right (4px inset from the left and bottom edges)
 *     - a cyan/100 (color/cyan/100) fill at 40% opacity, blend mode soft-light
 *     - a 650×506 box, soft-light, centred 4px left of centre, holding the 604×440 glare vector
 *       rotated 6.5°
 *
 * Everything is sized in percentages so the glare scales with the card (400px on desktop, 300px on
 * mobile). Nothing between the blending layers and the artwork may create a stacking context
 * (no transform, opacity or radius-clip on the wrapper), otherwise `soft-light` would blend against
 * nothing instead of the artwork underneath.
 */
export function HeroTokenCardFace({
  src,
  size,
  priority = true,
  cardRadius = 10,
}: {
  src: string;
  size: number;
  /** Preload the artwork. Off for the duplicate cards of the endless loop, which reuse the same files. */
  priority?: boolean;
  /** The px radius of the outer tile wrapper that clips this component (its `rounded-*` class), e.g.
   * `rounded-10` -> 10, `rounded-12` -> 12. The glare overlay is inset ~1% from the card's right/top
   * edges (see below), so its own radius has to be a few px smaller than the card's to stay concentric
   * with the card's corner — sharing the exact same radius as the (larger) card reads as a mismatch
   * once the overlay's corner sits a couple of px inside the card's true edge. This component is
   * shared by tiles whose wrapper uses different radii (10 on the hero sections, 12 on the contact
   * page's collage), so the delta is computed from whatever the caller's wrapper actually uses instead
   * of being a fixed number here. */
  cardRadius?: number;
}) {
  const glareRadius = Math.max(0, cardRadius - 4);
  return (
    <>
      <Image
        src={src}
        alt=""
        width={size}
        height={size}
        sizes={`${size}px`}
        // The hero is the first screen and these are its largest images (the LCP element), so
        // they are preloaded instead of lazily loaded.
        priority={priority}
        className="h-full w-full scale-[1.18] object-contain"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute right-0 top-0 overflow-hidden"
        style={{ width: '99%', height: '99%', borderRadius: glareRadius }}
      >
        <div className="absolute inset-0 bg-cyan-100 opacity-40 mix-blend-soft-light" />
        <div
          className="absolute flex items-center justify-center mix-blend-soft-light"
          style={{
            width: '164.2%',
            height: '127.7%',
            left: 'calc(50% - 1.01%)',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="relative" style={{ width: '92.94%', height: '87.03%', transform: 'rotate(6.5deg)' }}>
            <Image src="/images/hero-card-glare.svg" alt="" fill sizes="400px" className="max-w-none" />
          </div>
        </div>
      </div>
    </>
  );
}
